/** Safe arithmetic evaluator for table column formulas (no eval). */

export type FormulaColumnRef = {
  key: string;
  header?: string;
  index: number;
};

export type FormulaDateRange = {
  from?: string | Date | number | null;
  to?: string | Date | number | null;
  now?: string | Date | number | null;
  prevRow?: Record<string, unknown> | null;
  sums?: Record<string, number> | null;
  timeZone?: string | null;
};

const DEFAULT_ZONE = "Europe/Istanbul";

function toDate(value: FormulaDateRange["from"]): Date | null {
  if (value == null || value === "") return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function resolveTimeZone(timeZone?: string | null): string {
  const trimmed = timeZone?.trim();
  if (!trimmed) return DEFAULT_ZONE;
  try {
    Intl.DateTimeFormat("en-CA", { timeZone: trimmed }).format(new Date(0));
    return trimmed;
  } catch {
    return DEFAULT_ZONE;
  }
}

function zoneDateKey(date: Date, timeZone: string): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

function zoneParts(date: Date, timeZone: string): { year: number; month: number; day: number } {
  const [year, month, day] = zoneDateKey(date, timeZone).split("-").map(Number);
  return { year, month, day };
}

function zoneUtcMidnight(date: Date, timeZone: string): number {
  return Date.parse(`${zoneDateKey(date, timeZone)}T00:00:00Z`);
}

function localDateFromUtcMidnight(ms: number): Date {
  return new Date(ms);
}

export function inclusiveRangeDays(
  from?: FormulaDateRange["from"],
  to?: FormulaDateRange["to"],
  timeZone?: string | null
): number {
  const start = toDate(from);
  const end = toDate(to);
  if (!start || !end) return 0;
  const zone = resolveTimeZone(timeZone);
  const a = zoneUtcMidnight(start, zone);
  const b = zoneUtcMidnight(end, zone);
  return Math.abs(Math.round((b - a) / 86_400_000)) + 1;
}

export function rangeHours(from?: FormulaDateRange["from"], to?: FormulaDateRange["to"]): number {
  const start = toDate(from);
  const end = toDate(to);
  if (!start || !end) return 0;
  return Math.abs(end.getTime() - start.getTime()) / 3_600_000;
}

export function rangeMinutes(from?: FormulaDateRange["from"], to?: FormulaDateRange["to"]): number {
  const start = toDate(from);
  const end = toDate(to);
  if (!start || !end) return 0;
  return Math.abs(end.getTime() - start.getTime()) / 60_000;
}

export function rangeSeconds(from?: FormulaDateRange["from"], to?: FormulaDateRange["to"]): number {
  const start = toDate(from);
  const end = toDate(to);
  if (!start || !end) return 0;
  return Math.abs(end.getTime() - start.getTime()) / 1_000;
}

export function rangeWeekdays(
  from?: FormulaDateRange["from"],
  to?: FormulaDateRange["to"],
  timeZone?: string | null
): number {
  return countWeekdays(from, to, true, timeZone);
}

export function rangeWeekendDays(
  from?: FormulaDateRange["from"],
  to?: FormulaDateRange["to"],
  timeZone?: string | null
): number {
  return countWeekdays(from, to, false, timeZone);
}

export function rangeWeeks(
  from?: FormulaDateRange["from"],
  to?: FormulaDateRange["to"],
  timeZone?: string | null
): number {
  const start = toDate(from);
  const end = toDate(to);
  if (!start || !end) return 0;
  const zone = resolveTimeZone(timeZone);
  let a = zoneUtcMidnight(start, zone);
  let b = zoneUtcMidnight(end, zone);
  if (a > b) [a, b] = [b, a];
  const startMonday = mondayUtc(a);
  const endMonday = mondayUtc(b);
  return Math.round((endMonday - startMonday) / (7 * 86_400_000)) + 1;
}

export function rangeMonths(
  from?: FormulaDateRange["from"],
  to?: FormulaDateRange["to"],
  timeZone?: string | null
): number {
  const start = toDate(from);
  const end = toDate(to);
  if (!start || !end) return 0;
  const zone = resolveTimeZone(timeZone);
  const a = zoneParts(start, zone);
  const b = zoneParts(end, zone);
  return Math.abs(b.year * 12 + b.month - (a.year * 12 + a.month)) + 1;
}

export function rangeQuarters(
  from?: FormulaDateRange["from"],
  to?: FormulaDateRange["to"],
  timeZone?: string | null
): number {
  const start = toDate(from);
  const end = toDate(to);
  if (!start || !end) return 0;
  const zone = resolveTimeZone(timeZone);
  return Math.abs(yearQuarter(zoneParts(end, zone)) - yearQuarter(zoneParts(start, zone))) + 1;
}

export function rangeYears(
  from?: FormulaDateRange["from"],
  to?: FormulaDateRange["to"],
  timeZone?: string | null
): number {
  const start = toDate(from);
  const end = toDate(to);
  if (!start || !end) return 0;
  const zone = resolveTimeZone(timeZone);
  return Math.abs(zoneParts(end, zone).year - zoneParts(start, zone).year) + 1;
}

export function elapsedDays(
  from?: FormulaDateRange["from"],
  to?: FormulaDateRange["to"],
  now?: FormulaDateRange["now"],
  timeZone?: string | null
): number {
  const fromDate = toDate(from);
  const nowDate = toDate(now ?? Date.now());
  const toDateValue = toDate(to);
  if (!fromDate || !nowDate) return 0;
  const zone = resolveTimeZone(timeZone);
  const fromMs = zoneUtcMidnight(fromDate, zone);
  let endMs = zoneUtcMidnight(nowDate, zone);
  if (toDateValue) {
    const toMs = zoneUtcMidnight(toDateValue, zone);
    if (endMs > toMs) endMs = toMs;
  }
  if (endMs < fromMs) return 0;
  return Math.round((endMs - fromMs) / 86_400_000) + 1;
}

export function remainingDays(
  from?: FormulaDateRange["from"],
  to?: FormulaDateRange["to"],
  now?: FormulaDateRange["now"],
  timeZone?: string | null
): number {
  const fromDate = toDate(from);
  const nowDate = toDate(now ?? Date.now());
  const toDateValue = toDate(to);
  if (!toDateValue || !nowDate) return 0;
  const zone = resolveTimeZone(timeZone);
  const toMs = zoneUtcMidnight(toDateValue, zone);
  const nowMs = zoneUtcMidnight(nowDate, zone);
  if (fromDate) {
    const fromMs = zoneUtcMidnight(fromDate, zone);
    if (nowMs < fromMs) return Math.round((toMs - fromMs) / 86_400_000) + 1;
  }
  if (nowMs >= toMs) return 0;
  const remainingStart = nowMs + 86_400_000;
  if (remainingStart > toMs) return 0;
  return Math.round((toMs - remainingStart) / 86_400_000) + 1;
}

export function elapsedHours(
  from?: FormulaDateRange["from"],
  to?: FormulaDateRange["to"],
  now?: FormulaDateRange["now"]
): number {
  const start = toDate(from);
  const end = clampDate(toDate(now ?? Date.now()), start, toDate(to));
  if (!start || !end || end.getTime() < start.getTime()) return 0;
  return (end.getTime() - start.getTime()) / 3_600_000;
}

export function remainingHours(
  from?: FormulaDateRange["from"],
  to?: FormulaDateRange["to"],
  now?: FormulaDateRange["now"]
): number {
  const finish = toDate(to);
  const start = clampDate(toDate(now ?? Date.now()), toDate(from), finish);
  if (!finish || !start || start.getTime() > finish.getTime()) return 0;
  return (finish.getTime() - start.getTime()) / 3_600_000;
}

export function needsPreviousPeriod(formula?: string | null): boolean {
  if (!formula) return false;
  return /\{\{\s*(prev|delta|deltapct)\./i.test(formula);
}

export function headerToColumnKey(header: string, index: number): string {
  const slug = header
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return slug || `col_${index}`;
}

export function buildFormulaRefMap(columns: FormulaColumnRef[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const col of columns) {
    map.set(col.key.toLowerCase(), col.key);
    map.set(String(col.index), col.key);
    if (col.header?.trim()) {
      map.set(headerToColumnKey(col.header, col.index), col.key);
      map.set(col.header.trim().toLowerCase(), col.key);
    }
  }
  return map;
}

function tokenValue(
  token: string,
  refMap: Map<string, string>,
  row: Record<string, unknown>,
  range?: FormulaDateRange | null
): number | null {
  if (!range) return null;
  const normalized = token.toLowerCase().replace(/\s+/g, "");
  const now = range.now ?? Date.now();
  const zone = resolveTimeZone(range.timeZone);
  switch (normalized) {
    case "range.days":
    case "range.day":
    case "range.gun":
      return inclusiveRangeDays(range.from, range.to, zone);
    case "range.hours":
    case "range.hour":
    case "range.saat":
      return rangeHours(range.from, range.to);
    case "range.minutes":
    case "range.minute":
    case "range.dakika":
      return rangeMinutes(range.from, range.to);
    case "range.seconds":
    case "range.second":
    case "range.saniye":
      return rangeSeconds(range.from, range.to);
    case "range.weeks":
    case "range.week":
    case "range.hafta":
      return rangeWeeks(range.from, range.to, zone);
    case "range.months":
    case "range.month":
    case "range.ay":
      return rangeMonths(range.from, range.to, zone);
    case "range.quarters":
    case "range.quarter":
    case "range.ceyrek":
      return rangeQuarters(range.from, range.to, zone);
    case "range.years":
    case "range.year":
    case "range.yil":
      return rangeYears(range.from, range.to, zone);
    case "range.weekdays":
    case "range.weekday":
    case "range.workdays":
    case "range.workday":
    case "range.isgunu":
      return rangeWeekdays(range.from, range.to, zone);
    case "range.weekenddays":
    case "range.weekendday":
    case "range.haftasonu":
      return rangeWeekendDays(range.from, range.to, zone);
    case "range.elapseddays":
    case "range.elapsedday":
    case "range.gecengun":
      return elapsedDays(range.from, range.to, now, zone);
    case "range.remainingdays":
    case "range.remainingday":
    case "range.kalangun":
      return remainingDays(range.from, range.to, now, zone);
    case "range.elapsedhours":
    case "range.elapsedhour":
    case "range.gecensaat":
      return elapsedHours(range.from, range.to, now);
    case "range.remaininghours":
    case "range.remaininghour":
    case "range.kalansaat":
      return remainingHours(range.from, range.to, now);
    case "range.iscomplete":
    case "range.complete": {
      const end = toDate(range.to);
      const current = toDate(now);
      return end && current && current.getTime() > end.getTime() ? 1 : 0;
    }
    case "calendar.daysinmonth":
    case "calendar.aygun":
      return calendarDaysInMonth(range.to, now, zone);
    case "calendar.daysinyear":
    case "calendar.yilgun":
      return calendarDaysInYear(range.to, now, zone);
    case "calendar.dayofmonth":
    case "calendar.gun":
      return calendarParts(range.to, now, zone).day;
    case "calendar.month":
    case "calendar.ay":
      return calendarParts(range.to, now, zone).month;
    case "calendar.quarter":
    case "calendar.ceyrek":
      return quarterOf(calendarParts(range.to, now, zone).month);
    case "calendar.year":
    case "calendar.yil":
      return calendarParts(range.to, now, zone).year;
    case "calendar.iscurrentmonth":
      return isCurrentMonth(range.from, range.to, now, zone);
    case "const.hoursperday":
    case "const.saatgund":
      return 24;
    case "const.daysperweek":
    case "const.gunhafta":
      return 7;
    default:
      return prefixedTokenValue(normalized, token, refMap, row, range);
  }
}

function prefixedTokenValue(
  normalized: string,
  rawToken: string,
  refMap: Map<string, string>,
  row: Record<string, unknown>,
  range: FormulaDateRange
): number | null {
  const dot = normalized.indexOf(".");
  if (dot <= 0 || dot === normalized.length - 1) return null;
  const prefix = normalized.slice(0, dot);
  const name = rawToken.slice(rawToken.indexOf(".") + 1).trim();
  const key = refMap.get(name.toLowerCase()) ?? name;
  if (prefix === "sum") {
    const sums = range.sums ?? {};
    return sums[key] ?? sums[name.toLowerCase()] ?? 0;
  }
  if (prefix === "prev") {
    return toNumber(range.prevRow?.[key]);
  }
  if (prefix === "delta") {
    return toNumber(row[key]) - toNumber(range.prevRow?.[key]);
  }
  if (prefix === "deltapct") {
    const prev = toNumber(range.prevRow?.[key]);
    if (prev === 0) return 0;
    return ((toNumber(row[key]) - prev) / prev) * 100;
  }
  return null;
}

function toNumber(value: unknown): number {
  if (value == null || value === "") return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const num = Number(String(value).replace(",", ""));
  return Number.isFinite(num) ? num : 0;
}

const REF_PATTERN = /\{\{\s*([^}]+?)\s*\}\}/g;

function substituteRefs(
  formula: string,
  refMap: Map<string, string>,
  row: Record<string, unknown>,
  range?: FormulaDateRange | null
): string {
  return formula.replace(REF_PATTERN, (_, raw: string) => {
    const token = raw.trim();
    const special = tokenValue(token, refMap, row, range);
    if (special != null) return String(special);
    const key = refMap.get(token.toLowerCase()) ?? refMap.get(token) ?? token;
    return String(toNumber(row[key]));
  });
}

type Token =
  | { type: "number"; value: number }
  | { type: "op"; value: string }
  | { type: "ident"; value: string }
  | { type: "lparen" }
  | { type: "rparen" }
  | { type: "comma" };

function tokenize(expr: string): Token[] | null {
  const tokens: Token[] = [];
  let i = 0;
  while (i < expr.length) {
    const ch = expr[i];
    if (ch === " " || ch === "\t") {
      i += 1;
      continue;
    }
    if (ch === "(") {
      tokens.push({ type: "lparen" });
      i += 1;
      continue;
    }
    if (ch === ")") {
      tokens.push({ type: "rparen" });
      i += 1;
      continue;
    }
    if (ch === ",") {
      tokens.push({ type: "comma" });
      i += 1;
      continue;
    }
    const two = expr.slice(i, i + 2);
    if (two === ">=" || two === "<=" || two === "==" || two === "!=") {
      tokens.push({ type: "op", value: two });
      i += 2;
      continue;
    }
    if ("+-*/><".includes(ch)) {
      tokens.push({ type: "op", value: ch });
      i += 1;
      continue;
    }
    if (/[0-9.]/.test(ch)) {
      let j = i + 1;
      while (j < expr.length && /[0-9.]/.test(expr[j])) j += 1;
      const num = Number(expr.slice(i, j));
      if (!Number.isFinite(num)) return null;
      tokens.push({ type: "number", value: num });
      i = j;
      continue;
    }
    if (/[A-Za-z_]/.test(ch)) {
      let j = i + 1;
      while (j < expr.length && /[A-Za-z0-9_]/.test(expr[j])) j += 1;
      tokens.push({ type: "ident", value: expr.slice(i, j).toLowerCase() });
      i = j;
      continue;
    }
    return null;
  }
  return tokens;
}

class Parser {
  private pos = 0;

  constructor(private readonly tokens: Token[]) {}

  parse(): number | null {
    const value = this.parseComparison();
    if (value == null || this.pos !== this.tokens.length) return null;
    return value;
  }

  private parseComparison(): number | null {
    let value = this.parseExpression();
    if (value == null) return null;
    while (this.pos < this.tokens.length) {
      const token = this.tokens[this.pos];
      if (token.type !== "op" || !isCompareOp(token.value)) break;
      this.pos += 1;
      const rhs = this.parseExpression();
      if (rhs == null) return null;
      value = compare(token.value, value, rhs);
    }
    return value;
  }

  private parseExpression(): number | null {
    let value = this.parseTerm();
    if (value == null) return null;
    while (this.pos < this.tokens.length) {
      const token = this.tokens[this.pos];
      if (token.type !== "op" || (token.value !== "+" && token.value !== "-")) break;
      this.pos += 1;
      const rhs = this.parseTerm();
      if (rhs == null) return null;
      value = token.value === "+" ? value + rhs : value - rhs;
    }
    return value;
  }

  private parseTerm(): number | null {
    let value = this.parseFactor();
    if (value == null) return null;
    while (this.pos < this.tokens.length) {
      const token = this.tokens[this.pos];
      if (token.type !== "op" || (token.value !== "*" && token.value !== "/")) break;
      this.pos += 1;
      const rhs = this.parseFactor();
      if (rhs == null) return null;
      if (token.value === "/" && rhs === 0) return null;
      value = token.value === "*" ? value * rhs : value / rhs;
    }
    return value;
  }

  private parseFactor(): number | null {
    const token = this.tokens[this.pos];
    if (!token) return null;
    if (token.type === "number") {
      this.pos += 1;
      return token.value;
    }
    if (token.type === "op" && token.value === "-") {
      this.pos += 1;
      const inner = this.parseFactor();
      return inner == null ? null : -inner;
    }
    if (token.type === "ident") {
      this.pos += 1;
      return this.parseFunction(token.value);
    }
    if (token.type === "lparen") {
      this.pos += 1;
      const inner = this.parseComparison();
      if (inner == null) return null;
      const close = this.tokens[this.pos];
      if (!close || close.type !== "rparen") return null;
      this.pos += 1;
      return inner;
    }
    return null;
  }

  private parseFunction(name: string): number | null {
    const open = this.tokens[this.pos];
    if (!open || open.type !== "lparen") return null;
    this.pos += 1;
    const args: number[] = [];
    const next = this.tokens[this.pos];
    if (next?.type === "rparen") {
      this.pos += 1;
      return applyFunction(name, args);
    }
    while (true) {
      const arg = this.parseComparison();
      if (arg == null) return null;
      args.push(arg);
      const sep = this.tokens[this.pos];
      if (!sep) return null;
      if (sep.type === "comma") {
        this.pos += 1;
        continue;
      }
      if (sep.type === "rparen") {
        this.pos += 1;
        return applyFunction(name, args);
      }
      return null;
    }
  }
}

function isCompareOp(op: string): boolean {
  return op === ">" || op === "<" || op === ">=" || op === "<=" || op === "==" || op === "!=";
}

function compare(op: string, left: number, right: number): number {
  switch (op) {
    case ">":
      return left > right ? 1 : 0;
    case "<":
      return left < right ? 1 : 0;
    case ">=":
      return left >= right ? 1 : 0;
    case "<=":
      return left <= right ? 1 : 0;
    case "==":
      return left === right ? 1 : 0;
    case "!=":
      return left !== right ? 1 : 0;
    default:
      return 0;
  }
}

function applyFunction(name: string, args: number[]): number | null {
  switch (name) {
    case "round":
      if (args.length === 1) return Math.round(args[0]);
      if (args.length === 2) {
        const factor = 10 ** args[1];
        return Math.round(args[0] * factor) / factor;
      }
      return null;
    case "floor":
      return args.length === 1 ? Math.floor(args[0]) : null;
    case "ceil":
      return args.length === 1 ? Math.ceil(args[0]) : null;
    case "abs":
      return args.length === 1 ? Math.abs(args[0]) : null;
    case "min":
      return args.length === 2 ? Math.min(args[0], args[1]) : null;
    case "max":
      return args.length === 2 ? Math.max(args[0], args[1]) : null;
    case "clamp":
      return args.length === 3 ? Math.min(Math.max(args[0], args[1]), args[2]) : null;
    case "pow":
      return args.length === 2 ? args[0] ** args[1] : null;
    case "pct":
      if (args.length !== 2) return null;
      return args[1] === 0 ? 0 : (args[0] / args[1]) * 100;
    case "safediv":
      if (args.length !== 2) return null;
      return args[1] === 0 ? 0 : args[0] / args[1];
    case "if":
      return args.length === 3 ? (args[0] !== 0 ? args[1] : args[2]) : null;
    case "nullif":
      if (args.length !== 2) return null;
      return args[0] === args[1] ? null : args[0];
    case "coalesce":
      return args.length === 2 ? (args[0] !== 0 ? args[0] : args[1]) : null;
    default:
      return null;
  }
}

export function evaluateArithmeticExpression(expr: string): number | null {
  const trimmed = expr.trim();
  if (!trimmed) return null;
  const tokens = tokenize(trimmed);
  if (!tokens?.length) return null;
  return new Parser(tokens).parse();
}

export function evaluateColumnFormula(
  formula: string,
  row: Record<string, unknown>,
  columns: FormulaColumnRef[],
  range?: FormulaDateRange | null
): number | null {
  const trimmed = formula.trim();
  if (!trimmed) return null;
  const refMap = buildFormulaRefMap(columns);
  const substituted = substituteRefs(trimmed, refMap, row, range);
  return evaluateArithmeticExpression(substituted);
}

function countWeekdays(
  from?: FormulaDateRange["from"],
  to?: FormulaDateRange["to"],
  weekdays = true,
  timeZone?: string | null
): number {
  const start = toDate(from);
  const end = toDate(to);
  if (!start || !end) return 0;
  const zone = resolveTimeZone(timeZone);
  let a = zoneUtcMidnight(start, zone);
  let b = zoneUtcMidnight(end, zone);
  if (a > b) [a, b] = [b, a];
  let count = 0;
  for (let ms = a; ms <= b; ms += 86_400_000) {
    const dow = localDateFromUtcMidnight(ms).getUTCDay();
    const weekend = dow === 0 || dow === 6;
    if (weekdays !== weekend) count += 1;
  }
  return count;
}

function mondayUtc(ms: number): number {
  const dow = localDateFromUtcMidnight(ms).getUTCDay();
  const offset = dow === 0 ? 6 : dow - 1;
  return ms - offset * 86_400_000;
}

function yearQuarter(parts: { year: number; month: number }): number {
  return parts.year * 4 + quarterOf(parts.month);
}

function quarterOf(month: number): number {
  return Math.floor((month - 1) / 3) + 1;
}

function calendarParts(
  to?: FormulaDateRange["to"],
  now?: FormulaDateRange["now"],
  timeZone?: string | null
): { year: number; month: number; day: number } {
  const date = toDate(to) ?? toDate(now ?? Date.now()) ?? new Date();
  return zoneParts(date, resolveTimeZone(timeZone));
}

function calendarDaysInMonth(
  to?: FormulaDateRange["to"],
  now?: FormulaDateRange["now"],
  timeZone?: string | null
): number {
  const parts = calendarParts(to, now, timeZone);
  return new Date(Date.UTC(parts.year, parts.month, 0)).getUTCDate();
}

function calendarDaysInYear(
  to?: FormulaDateRange["to"],
  now?: FormulaDateRange["now"],
  timeZone?: string | null
): number {
  const year = calendarParts(to, now, timeZone).year;
  const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  return leap ? 366 : 365;
}

function isCurrentMonth(
  from?: FormulaDateRange["from"],
  to?: FormulaDateRange["to"],
  now?: FormulaDateRange["now"],
  timeZone?: string | null
): number {
  const zone = resolveTimeZone(timeZone);
  const current = calendarParts(now, now, zone);
  const start = toDate(from);
  const end = toDate(to);
  if (!start || !end) return 0;
  const a = zoneParts(start, zone);
  const b = zoneParts(end, zone);
  return a.year === current.year && a.month === current.month && b.year === current.year && b.month === current.month
    ? 1
    : 0;
}

function clampDate(value: Date | null, from: Date | null, to: Date | null): Date | null {
  if (!value) return from;
  let clamped = value;
  if (from && clamped.getTime() < from.getTime()) clamped = from;
  if (to && clamped.getTime() > to.getTime()) clamped = to;
  return clamped;
}
